import { useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useWorkspaceStore } from "@/store/workspace-store";
import { Loader2 } from "lucide-react";

interface BulkStatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDealIds: string[];
  onStatusUpdate: (status: string) => Promise<void>;
  isLoading?: boolean;
}

export const BulkStatusUpdateModal = ({
  isOpen,
  onClose,
  selectedDealIds,
  onStatusUpdate,
  isLoading = false,
}: BulkStatusUpdateModalProps) => {
  const { activeWorkspace, getStatusColor } = useWorkspaceStore();
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  const handleUpdate = async () => {
    if (!selectedStatus) return;
    await onStatusUpdate(selectedStatus);
    onClose();
  };

  const handleClose = () => {
    setSelectedStatus("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Update Status for {selectedDealIds.length} Deals
          </DialogTitle>
          <DialogDescription>
            Select a new status to apply to all selected deals. This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">New Status</label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  disabled={isLoading}
                >
                  {selectedStatus || "Select a status"}
                  <svg
                    className="ml-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-full min-w-[200px]">
                <DropdownMenuRadioGroup
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  {(activeWorkspace?.statuses || []).map((status) => (
                    <DropdownMenuRadioItem
                      key={status.title}
                      value={status.title}
                      style={
                        status.colour?.startsWith("#")
                          ? {
                              backgroundColor: status.colour,
                              color: "white",
                            }
                          : undefined
                      }
                      className={`${
                        !status.colour?.startsWith("#")
                          ? getStatusColor(status.title)
                          : ""
                      }`}
                    >
                      {status.title}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={!selectedStatus || isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
